import React, { useEffect, useState } from "react";

import { Tooltip } from "@mui/material";
import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiEye } from "react-icons/fi";
import { Badge } from "reactstrap";

import Breadcrumb from "../../../../../../components/Generic/Breadcrump";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../context/StateContext";
import { Country } from "../../../../../../models/country";
import { CompanyPort } from "../../../../../../models/port";
import { Row } from "../../../../../../models/shipeezi.table";
import { Status } from "../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../util/onSaveItemSelected";
import PortModal from "./PortModal";
import useStyles from "./styles";
import { useActions } from "./useActions";

type Props = {
  permission?: {
    edit: boolean;
  };
};

const PortPage = (props: Props) => {
  const { permission } = props;
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const {
    variables,
    switchStatus,
    setPageSize,
    setPort,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
  } = useActions();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [isOnViewMode, setIsOnViewMode] = useState<boolean>(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`${classes.statusBadge} ${
        status === Status.ACTIVE ? classes.statusBadgeActive : ""
      }`}
    >
      {status}
    </Badge>
  );

  const viewPort = (index: number) => (
    <Tooltip title="View" placement="top">
      <div>
        <FiEye
          className={classes.button}
          size={18}
          onClick={() => {
            if (variables.hasErrors !== undefined) setHasErrors(undefined);
            const findPort = variables.ports.find((port) => Number(port.id) === Number(index));

            if (findPort) {
              setPort(findPort);
              setIsOnViewMode(true);
              setOpenModal(true);
            }
          }}
        />
      </div>
    </Tooltip>
  );

  const changePortStatus = (index: number) => {
    const findPort = variables.ports.find((port) => Number(port.id) === Number(index));

    if (findPort) {
      switchStatus({
        companyPort: {
          companyId: currentUser?.companyId,
          portId: findPort?.id,
        },
      });
    }
  };

  const handleSavePortSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, variables.ports);

    setRows([...rowsSelected]);
  };

  const columns: Column[] = [
    { name: "city", title: translate("cityCode") },
    { name: "site", title: translate("site") },
    { name: "siteCode", title: translate("siteCode"), searchIdentifier: "site_code" },
    {
      name: "country",
      title: translate("country"),
      formatText: (country: Country) => country?.name,
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "locationName",
      title: translate("locationName"),
      hideSearch: false,
      searchIdentifier: "location_name",
    },
    {
      name: "companyPorts",
      title: translate("status"),
      formatComponent: (companyPorts?: CompanyPort[]) =>
        badgeStatus(companyPorts?.length ? Status.ACTIVE : Status.INACTIVE),
      hideSearch: true,
    },
  ];

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
    },

    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("status"),
          column: "status",
          options: [Status.ACTIVE, Status.INACTIVE],
        },
        {
          name: translate("code"),
          column: "code",
        },
        {
          name: translate("name"),
          column: "name",
        },
      ],
      onChange: handleFilter,
    },
  };

  useEffect(() => {
    if (variables.hasErrors !== undefined && !variables.hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.success.message.ports.id",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [variables.hasErrors]);

  return (
    <div data-cy="portDataList" className={classes.wrapper}>
      {/* <header className="mb-4">
        <Breadcrumb
          items={[
            { title: translate("configurations") },
            {
              title: translate("ports"),
              color: "#465869",
              dataCy: "breadcrumbsSubTitle",
            },
          ]}
        />
      </header> */}

      <ShipeeziNewGrid
        data={variables.ports}
        columns={columns}
        totalItems={variables.total}
        loadingData={variables.isFetching || variables.isUpdating}
        emptyData={translate("noInformationToBeShown")}
        pageSize={variables.pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={variables.page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        clearSelection={clearSelection}
        handleSelect={(rows: number[]) => handleSavePortSelected(rows)}
        rowActions={{
          orderBy: ["handleView", "handleChange"],
          handleChange: (portId) => changePortStatus(Number(portId)),
        }}
        rowActionsExtra={[{ title: "View", key: "handleView", buttonAndAction: viewPort }]}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={generalActions}
      />

      {openModal && (
        <PortModal
          port={variables.port}
          open={openModal}
          hasErrors={variables.hasErrors}
          isLoading={variables.isUpdating}
          isOnViewMode={isOnViewMode}
          closeModal={() => setOpenModal(false)}
        />
      )}
    </div>
  );
};

export default PortPage;
